import {
	IS4Features,
	S4_OPT_IN_FEATURES,
	S4Feature,
	S4FeaturesKeys,
	S4OptInFeature
} from 'types/api';

export const isS4OptInFeature = (
	feature: S4Feature
): feature is S4OptInFeature =>
	(S4_OPT_IN_FEATURES as readonly S4Feature[]).includes(feature);

// Absent means on for an opt-out feature, off for an opt-in one.
export const isS4FeatureEnabled = (
	features: IS4Features | undefined,
	feature: S4Feature
): boolean => {
	const key = (
		isS4OptInFeature(feature) ? `${feature}Enabled` : `${feature}Disabled`
	) as S4FeaturesKeys;
	const stored = (features || {})[key];
	return isS4OptInFeature(feature) ? !!stored : !stored;
};

// Features that need a user, so they are inert on a connection that creates none. User attribute
// mapping is gated the same way but is not a suite feature, so it hides inside its own component.
// IdP setup and the test page are not here: they are what such a connection is for.
const AUTHENTICATION_ONLY_HIDDEN_FEATURES: readonly S4Feature[] = [
	S4Feature.Scim,
	S4Feature.Xaa,
	S4Feature.GroupMapping,
	S4Feature.RoleMapping,
	S4Feature.FgaMapping,
	S4Feature.SsoDomains,
	S4Feature.JitGuide
];

// A feature applies when the suite configuration enabled it and this connection can use it.
export const isFeatureEnabledForConfig = (
	configured: boolean,
	authenticationOnly: boolean,
	feature: S4Feature
): boolean =>
	configured &&
	!(
		authenticationOnly && AUTHENTICATION_ONLY_HIDDEN_FEATURES.includes(feature)
	);

// groupMappingDisabled disables both role and FGA mapping. Fold it into the two
// explicit flags so the rest of the app only reads roleMappingDisabled /
// fgaMappingDisabled.
export const normalizeMappingFeatures = (
	features?: IS4Features
): IS4Features | undefined =>
	features?.groupMappingDisabled
		? { ...features, roleMappingDisabled: true, fgaMappingDisabled: true }
		: features;

export const isRoleMappingDisabled = (features?: IS4Features): boolean =>
	features?.roleMappingDisabled ?? false;

export const isFgaMappingDisabled = (features?: IS4Features): boolean =>
	features?.fgaMappingDisabled ?? false;

// The group attribute feeds both role and FGA mapping, so it stays relevant
// while either one is enabled.
export const isGroupMappingDisabled = (features?: IS4Features): boolean =>
	isRoleMappingDisabled(features) && isFgaMappingDisabled(features);
